import i18n from '@i18n/index';
import { parseApiError } from '@shared/lib/api-error';
import { notification } from 'antd';

import type { NotificationProvider } from '@refinedev/core';

type NotificationKind = 'success' | 'error' | 'progress';

/**
 * Подбирает локализованный текст для ошибки.
 *
 * Алгоритм:
 * 1. Если `message` сам по себе — известный нам код (`OTP_EXPIRED`, `NO_ADMIN_ACCESS`, …) —
 *    берём `errors:<message>`.
 * 2. Иначе если `description` похоже на `ApiError` — парсим его `code`.
 * 3. Fallback — `errors:UNKNOWN_ERROR`.
 *
 * Refine `useLogin`/`useUpdate` и т.п. при `success: false` зовут `notification.open`
 * с `message = error.message` (куда мы кладём `code`) и `description = error.name`.
 * А ошибки от data-provider приходят с `description = ApiError`. Этот код покрывает оба случая.
 */
function localizeError(message?: string, description?: unknown): { title: string; body: string } {
  const messageKey = message ? `errors:${message}` : null;
  if (messageKey && i18n.exists(messageKey)) {
    return { title: '', body: i18n.t(messageKey) };
  }
  const parsed = parseApiError(description, undefined);
  const key = `errors:${parsed.code}`;
  const params = parsed.params ?? {};
  const text = i18n.exists(key) ? i18n.t(key, params) : i18n.t('errors:UNKNOWN_ERROR');
  return {
    title: message && !messageKey ? message : '',
    body: text,
  };
}

/**
 * Notification-провайдер Refine на базе AntD `notification`.
 *
 * - `success` — короткий toast с заголовком.
 * - `error` — заголовок + локализованное сообщение из errors-bundle.
 * - `progress` — пропускается (Refine использует для прогресс-баров; AntD они не нужны).
 */
export const notificationProvider: NotificationProvider = {
  open: ({
    key,
    message,
    description,
    type,
  }: {
    key?: string;
    message: string;
    description?: string;
    type: NotificationKind;
  }) => {
    if (type === 'progress') return;
    if (type === 'error') {
      const { title, body } = localizeError(message, description);
      notification.error({
        key,
        message: title || i18n.t('common:states.empty'),
        description: body,
        placement: 'topRight',
        duration: 6,
      });
      return;
    }
    notification.success({
      key,
      message,
      description,
      placement: 'topRight',
    });
  },
  close: (key: string) => {
    notification.destroy(key);
  },
};
